import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// utils
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, concatMap, map, mergeMap, tap } from 'rxjs/operators';
import { concat, forkJoin, Observable, of } from 'rxjs';
import {
  EarningTotals,
  EmployeePay,
  HistoryVoidReportLine,
  LaborAllocation,
  ManualCheck,
  MasterPayrollObject,
  PaylineType,
  PayrollApprovalTotals,
  PayrollConfigSetupData,
  PayrollStatuses,
  PayTypes,
  TaxHierarchy,
  ThirdPartyPayeeReport
} from '../../../models/payroll';
import { CompanyLocalTaxConfig, CompanyTaxCodeConfig } from '../../../models/tax-config';
import { PayrollService } from '../../../services/payroll.service';
import { CompaniesService } from '../../../services/companies.service';
import { AuditService } from '../../../services/audit.service';
import { AdminService } from '../../../services/admin.service';
import { FlattenResponseMessagesByTypes } from '../../../utils/api-response-helper';
import { ErrorDetail, WarningLevel } from '../../../models/error-detail';
import { ApiResponse } from '../../../models/api-response';
import { PayrollManagement } from '../../../tools/payroll-management';
import {
  PayrollSummary,
  PayrollSummaryOrderedLists,
  PayrollSummaryService,
  TaxCodeAndType
} from '../../../services/payroll-summary.service';
import { GroupBy, MapBy } from '../../../utils/math';
import { AclService } from '../../../services/acl.service';
import { IsPayDateInvalid } from '../payroll-landing-page/PaydateValidationFunction';
import { GetTimeInEST } from '../../../utils/dateUtils';
import { BusinessDaysService } from '../../../services/business-days.service';
import { AuthService } from '../../../services/auth.service';
import { ModalService } from '../../../services/modal.service';
import { DecimalPipe } from '@angular/common';
import { Client, ClientSetup } from '../../../models/client';
import { KeyPair } from '../../../models/base';
import { InstanceTypes } from '../../../models/instance-types';
import { ModalStates } from '../../../models/modal';
import { IPayrollEventData } from '../../../models/audit-action';
import { LaborAllocationTypes } from '../../../models/labor-allocation';
import { Division } from '../../../models/division';

@Component({
  selector: 'payroll-preview',
  templateUrl: './payroll-preview.component.html',
  styleUrls: ['./payroll-preview.component.css'],
  standalone: false
})
export class PayrollPreviewComponent implements OnInit {
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    // bug 13993, our current guard is not invoked when the browser window itself is closed/disposed
    // so in the event the user is on the grid and just terminates thier browser window
    // we need to initiate a status on hold request
    // otherwise anyone visting the payroll thereafter will be met
    // with a payroll in progress critical error and will be prevented from resuming...
    return this.payrollMgmt.onPayrollDeactivate(this.payroll, false);
  }

  public client: Client;
  public clientCode: string;
  public configSetupData: PayrollConfigSetupData;
  public thirdPartyPayeeReports: ThirdPartyPayeeReport[] = [];
  public hasCriticalErrors: boolean = false;

  public freqDisplay: string;
  // prevent elements from flicking in/out before everything loads
  public loaded: boolean = false;
  public seqNo: number;
  public year: number;
  public payroll: MasterPayrollObject;
  public periodBegin: Date;

  // payroll approval summary
  public approveTotals: Record<string, number> = {};
  public responseMessages: any[];
  public isPayrollApproved: boolean = true;
  public payrollApprovalTotals: PayrollApprovalTotals;
  public debitApprovalSummary = [];

  public earningsCollapsed = true;
  public earningTotals: Record<string, EarningTotals> = {};

  public jobCodes: KeyPair[];
  public totalTaxesCollapsed = true;
  public taxHierarchies: TaxHierarchy[] = [];

  public taxConfigs: CompanyTaxCodeConfig[] = [];
  public localTaxConfigs: CompanyLocalTaxConfig[] = [];
  public masterTaxMap: TaxCodeAndType[] = [];
  public divisions: Division[] = [];

  public totalDeductionsCollapsed = true;

  public totalNetCollapsed = true;

  public payrollSummary: PayrollSummary;
  public orderedLists: PayrollSummaryOrderedLists;
  public clientSetup: ClientSetup;
  public taxObligation: number = 0;
  public taxObligationMessage: string;

  private cutoffDate: Date;
  private isCsr = false;
  get isClientPortal() {
    return this.env.instanceType == InstanceTypes.ClientPortal;
  }

  get math() {
    return Math;
  }

  public canSubmitPayroll = false;
  public hasRecentHistoryVoids = false;
  constructor(
    private decimalPipe: DecimalPipe,
    private payrollService: PayrollService,
    private companyService: CompaniesService,
    private ngModalService: NgbModal,
    public payrollMgmt: PayrollManagement,
    private auditService: AuditService,
    private adminService: AdminService,
    private route: ActivatedRoute,
    private router: Router,
    private payrollSummaryService: PayrollSummaryService,
    private aclService: AclService,
    private businessDaysService: BusinessDaysService,
    private authService: AuthService,
    private modalService: ModalService,
    @Inject('env') private env: any
  ) {}

  ngOnInit(): void {
    this.clientCode = this.route.snapshot.paramMap.get('client_code');
    this.year = +this.route.snapshot.paramMap.get('year');
    this.seqNo = +this.route.snapshot.paramMap.get('seqNo');

    forkJoin([
      this.adminService.getTaxHierarchy().pipe(map(data => (this.taxHierarchies = data))),
      this.companyService.getCompany(this.clientCode).pipe(map(data => (this.client = data))),
      this.companyService
        .getTaxConfigs(this.clientCode, true)
        .pipe(map(data => (this.taxConfigs = data))),
      this.companyService
        .getLocalTaxConfigs(this.clientCode, null, null)
        .pipe(map(data => (this.localTaxConfigs = data))),
      this.companyService.getJobCodes(this.clientCode).pipe(map(data => (this.jobCodes = data))),
      this.payrollMgmt.getPayroll(this.clientCode, this.year, this.seqNo, true).pipe(
        concatMap(data => {
          this.payroll = data;
          return this.payrollService
            .getOutstandingTaxObligation(this.clientCode, this.year, this.seqNo)
            .pipe(tap(tax => (this.taxObligation = tax)));
        })
      ),
      this.companyService.getLocations(this.clientCode).pipe(
        concatMap(locations => {
          return this.companyService.getClientSetup(this.clientCode).pipe(
            map(resp => {
              this.clientSetup = resp;
              return locations;
            })
          );
        })
      ),
      this.companyService.getDivisions(this.clientCode).pipe(
        map(resp => {
          this.divisions = resp;
        })
      )
    ]).subscribe(() => {
      // check if payroll is on hold or
      // current user is not the active payroll user
      if (!this.payrollMgmt.canUserAccessPayroll(this.payroll)) {
        this.router.navigate([`/client/${this.clientCode}/payroll`]);
      }

      this.masterTaxMap = this.payrollSummaryService.BuildMasterTaxMap(
        this.taxHierarchies,
        this.taxConfigs,
        this.localTaxConfigs
      );

      this._fetchConfigSetupData();
      this._setPayFrequency();
      this.aclService.CurrentUserHasMinimumRole('CSR').subscribe(result => {
        this.isCsr = result;
        if (!this.isCsr) {
          this._getDueDate().subscribe(cutoffDate => {
            //This returns true if the pay date is invalid, meaning that
            //the pay date is AT LEAST yesterday. In this case,
            //nobody can submit payrolls.
            const isPayDateInvalid = IsPayDateInvalid(this.payroll);
            if (isPayDateInvalid) {
              this.canSubmitPayroll = false;
            }
            //if we hit this point, then we are dealing with a CP user
            //A CP user can only submit payrolls up to the payroll cutoff time on
            //the pay date

            const eod = this.env.endOfDayCutoff;
            const cutoffDateWithEod = GetTimeInEST(
              eod.hours,
              eod.minutes,
              cutoffDate.PrefundStartDate
            );
            const now = new Date();
            this.canSubmitPayroll = now < cutoffDateWithEod && !cutoffDate.IsInPrefund;
            this.cutoffDate = cutoffDateWithEod;
            this._checkTaxObligationThreshold();
          });
        } else {
          //CSRs can always submit as long as the pay date is not in the past.
          this.canSubmitPayroll = !IsPayDateInvalid(this.payroll);
          this._checkTaxObligationThreshold();
        }
      });
    });
  }

  // #region PUBLIC METHODS

  public closeModal() {
    // if user clicked the close of the modal, we need to route them properly
    this.ngModalService.dismissAll();

    if (this.payrollApprovalTotals) {
      this.router.navigate([`/client/${this.clientCode}`]);
    } else {
      this.router.navigate([`/client/${this.clientCode}/payroll/${this.year}/${this.seqNo}`]);
    }
  }

  public collapseEarnings() {
    this.earningsCollapsed = !this.earningsCollapsed;
  }

  public collapseTotalTaxes() {
    this.totalTaxesCollapsed = !this.totalTaxesCollapsed;
  }

  public collapseTotalDeductions() {
    this.totalDeductionsCollapsed = !this.totalDeductionsCollapsed;
  }

  public collapseTotalNet() {
    this.totalNetCollapsed = !this.totalNetCollapsed;
  }

  public print() {
    window.print();
  }

  public showHistoryVoidMessage(value: boolean) {
    this.hasRecentHistoryVoids = value;
  }

  public submitApproval(content) {
    if (this.authService.isInAnyRole(['CSR1', 'RunPayroll', 'Client Portal Admin'])) {
      const now = new Date();
      if (this.cutoffDate == null) {
        this._getDueDate().subscribe(cutoffDate => {
          const eod = this.env.endOfDayCutoff;
          this.cutoffDate = GetTimeInEST(eod.hours, eod.minutes, cutoffDate.PrefundStartDate);
          this.canSubmitPayroll = !cutoffDate.IsInPrefund;
          this.submitApproval(content);
        });
      } else if (now >= this.cutoffDate) {
        this.canSubmitPayroll = false;
        this.modalService.showAlertModal(
          'Payroll is overdue. Please contact your Payroll Specialist to submit this payroll. Changes made on the Payroll Grid are saved.',
          ModalStates.Error,
          undefined,
          undefined,
          undefined,
          true
        );
      } else {
        this._handleSubmit(content);
      }
    } else {
      this._handleSubmit(content);
    }
  }

  // #endregion

  // #region PRIVATE METHODS

  private _auditPayrollComplete(
    masterAuditData: IPayrollEventData,
    auditWarningsAndErrorProperties
  ) {
    this.auditService.auditPayroll(`payroll-approval-complete`, masterAuditData, {
      ApprovalResult: this.payrollApprovalTotals ? 'Success' : 'Failure',
      Year: this.payroll.Year,
      ...auditWarningsAndErrorProperties
    });
  }

  private _auditManualChecks() {
    return this.companyService
      .getManualChecks(this.clientCode, this.payroll.LocationId.toString())
      .subscribe(checks => {
        let skippedChecks: ManualCheck[] = [];

        var empChecks = GroupBy(checks, check => check.Name);

        this.payroll.EmployeePays.forEach(empPay => {
          const employeeName = `${empPay.EmployeeFirstName || ''}${
            empPay.EmployeeMiddleName ? ' ' + empPay.EmployeeMiddleName : ''
          } ${empPay.EmployeeLastName || ''}`.trim();

          if (empChecks[employeeName] != null) {
            // Note: this is not a reliable way to match paylines to checks, but there is no exact way to match
            // them so we test for the same name and pay amount and make sure the payline is a manual check
            // and it is okay to pay - bbarrett 20230209

            empChecks[employeeName].forEach(check => {
              if (
                !empPay.Paylines.some(
                  payline =>
                    payline.NetAmountPaid == check.Total &&
                    payline.PaylineType == PaylineType.Manual &&
                    payline.OkToPay
                )
              ) {
                skippedChecks.push(check);
              }
            });
          }
        });

        skippedChecks.forEach(skippedCheck => {
          this.auditService.auditPayroll(
            'payroll-manual-check-skipped',
            {
              ClientCode: this.clientCode,
              PayrollId: this.payroll.Id,
              Calendars: this.payroll.Calendars,
              PayDate: this.payroll.PayDate,
              LocationId: this.payroll.LocationId,
              SequenceNumber: this.seqNo,
              PayrollType: this.payroll.PayType
            },
            {
              PersonFullName: skippedCheck.Name,
              NetCheckAmount: skippedCheck.Total,
              CheckNumber: skippedCheck.CheckNumber,
              CreatedOn: skippedCheck.DateCreated
            }
          );
        });
      });
  }

  private _auditSubmitApproval() {
    // task 8264 - fetch an array of payline errors of type 'Warning' and record an audit event
    let payrollWarnings = this.payroll.EmployeePays.filter(pay => {
      return pay.Paylines.some(line => {
        if (!line.Errors) {
          return null;
        }
        return line.Errors.filter(error => error.WarningLevel == WarningLevel.Warning);
      });
    }).flatMap(pay => pay.Paylines.flatMap(line => line.Errors).filter(x => x));

    let auditWarningProperties = {};
    for (let i = 0; i < payrollWarnings.length; i++) {
      let warningLabel = `Warning${i.toString().padStart(2, '0')}`;
      auditWarningProperties[warningLabel] = payrollWarnings[i].Message;
    }

    this.auditService.auditPayroll(
      `payroll-approval-submitted`,
      {
        ClientCode: this.clientCode,
        PayrollId: this.payroll.Id,
        Calendars: this.payroll.Calendars,
        PayDate: this.payroll.PayDate,
        LocationId: this.payroll.LocationId,
        SequenceNumber: this.seqNo,
        PayrollType: this.payroll.PayType,
        ...auditWarningProperties
      },
      {
        Year: this.year
      }
    );
  }

  private _auditSubmitApprovalResponse(response: ApiResponse<PayrollApprovalTotals>) {
    // task 8263 - audit submit approval response result and any errors and warnings
    let payrollWarningsAndErrors = FlattenResponseMessagesByTypes(
      [WarningLevel.Critical, WarningLevel.Warning],
      response.Messages
    );

    let auditWarningsAndErrorProperties = {};
    let warningTracker = 1;
    let errorTracker = 1;

    for (let msg of payrollWarningsAndErrors) {
      if (msg.WarningLevel == 'Critical') {
        let errorLabel = `Critical${errorTracker.toString().padStart(2, '0')}`;
        errorTracker++;

        auditWarningsAndErrorProperties[errorLabel] = msg.Message;
      } else if (msg.WarningLevel == 'Warning') {
        let warningLabel = `Warning${warningTracker.toString().padStart(2, '0')}`;
        warningTracker++;

        auditWarningsAndErrorProperties[warningLabel] = msg.Message;
      }
    }

    const masterAuditData: IPayrollEventData = {
      ClientCode: this.clientCode,
      PayrollId: this.payroll.Id,
      PayDate: this.payroll.PayDate,
      LocationId: this.payroll.LocationId,
      SequenceNumber: this.seqNo,
      PayrollType: this.payroll.PayType,
      Calendars: this.payroll.Calendars
    };

    // task 13630 - audit manual checks, if any
    const obs: Observable<any>[] = [];
    let hasManualChecks = false;
    this.payroll.EmployeePays.forEach(empPay => {
      const linesWithManualChecks = empPay.Paylines.filter(
        line => line.PaylineType === PaylineType.Manual
      );

      if (linesWithManualChecks && linesWithManualChecks.length > 0) {
        hasManualChecks = true;
        const employeeName = this.payrollMgmt.getEmployeeFullName(empPay);

        // bug 14497 - ensure that all manual check used audit events
        // are fired before the payroll complete audit event
        linesWithManualChecks.forEach(line => {
          obs.push(
            this.auditService.auditPayrollWithResponse(
              `payroll-manual-check-used`,
              masterAuditData,
              {
                PersonId: empPay.EmployeeNumber,
                PersonFullName: employeeName,
                NetCheckAmount: line.NetCheckAmountPaid
              }
            )
          );
        });
      }
    });

    // task 20119 Audit Labor Allocations
    if (this.client.LaborAllocationType != LaborAllocationTypes.None) {
      for (let empPay of this.payroll.EmployeePays) {
        const employeeName = this.payrollMgmt.getEmployeeFullName(empPay);

        for (let line of empPay.Paylines) {
          if (!line.OkToPay) {
            continue;
          }

          if (line.Allocations == null || Object.values(line.Allocations).length == 0) {
            // We have no allocations on this payline.
            // I don't think this should ever happen, but catch anyway for safety.
            //   -bbarrett 2023-04-28
            continue;
          }

          if (
            !Object.values(line.Allocations).some(
              alloc =>
                alloc.TemporaryHourlyAllocations != null ||
                alloc.TemporaryPercentageAllocations != null
            )
          ) {
            // All allocations are permanent, skip this payline.
            continue;
          }

          let defaultKey = '0';
          if (this.client.LaborAllocationType == LaborAllocationTypes['Department']) {
            defaultKey = `${empPay.DepartmentId}`;
          }

          if (
            Object.keys(line.Allocations).length == 1 &&
            Object.keys(line.Allocations)[0] == defaultKey &&
            line.Allocations[defaultKey].TemporaryPercentageAllocations == 100 &&
            line.Allocations[defaultKey].PermanentPercentageAllocations == null
          ) {
            // There was a 100% allocation to job code 'None' or the employee's default department,
            // we don't need to log this unless there is a different permanent percentage for the home allocation.
            continue;
          }

          obs.push(
            this.auditService.auditPayrollWithResponse('labor-allocation-saved', masterAuditData, {
              PersonId: empPay.EmployeeNumber,
              PersonFullName: employeeName,
              PaylineId: line.PaylineId,
              NetCheckAmount: line.NetCheckAmountPaid,
              AllocationType: this.client.LaborAllocationType,
              AllocationsJSON: JSON.stringify(line.Allocations),
              JobCodes: JSON.stringify(this.jobCodes),
              Earnings: JSON.stringify(this.configSetupData.Earnings)
            })
          );
        }
      }
    }

    if (obs.length) {
      forkJoin(obs).subscribe(() => {
        // bug 14497 - ensure that all manual check used audit events
        // are fired before the payroll complete audit event
        this._auditPayrollComplete(masterAuditData, auditWarningsAndErrorProperties);
      });
    } else {
      this._auditPayrollComplete(masterAuditData, auditWarningsAndErrorProperties);
    }
  }

  private _calculateApprovalTotals() {
    // debit totals
    this.approveTotals['DebitTotal'] =
      this.payrollApprovalTotals.BillingDebit +
      this.payrollApprovalTotals.PayrollDebit +
      this.payrollApprovalTotals.TaxFilingDebit +
      this.payrollApprovalTotals.ThirdPartyDisbursementAch;

    // check totals
    this.approveTotals['CheckTotal'] =
      this.payrollApprovalTotals.NetChecks +
      this.payrollApprovalTotals.ThirdPartyDisbursementChecks;
  }

  private _checkTaxObligationThreshold() {
    const threshold = this.env.taxObligationThreshold || 0;

    if (this.taxObligation < threshold) {
      return;
    }

    if (![PayTypes.Regular, PayTypes.OffCycle].includes(this.payroll.PayType)) {
      return;
    }

    // this is a 'tax filing' client if TaxFilingServiceEffectiveDate is not null and in the past
    const filingService = this.clientSetup.TaxFilingServiceEffectiveDate;
    const taxFiling =
      filingService != null && new Date(filingService).getTime() < new Date().getTime();

    const thresholdBaseMsg = `<strong>Federal 941 taxes due including those in this payroll are over $${this.decimalPipe.transform(
      threshold
    )}.</strong> These funds must be paid on the first business day after the pay date.`;

    this.taxObligationMessage = thresholdBaseMsg;

    if (this.env.instanceType !== InstanceTypes.CsrPortal && taxFiling) {
      // if client portal user and this is a 'tax filing' client, disable payroll submit
      this.canSubmitPayroll = false;
    }
  }

  private _fetchConfigSetupData() {
    this.payrollService
      .getPayrollConfigSetupData(
        this.clientCode,
        this.payroll.Calendars,
        this.payroll.LocationId,
        this.payroll.PayDate,
        this.payroll.PayType
      )
      .subscribe(config => {
        this.configSetupData = config;

        this._hideAllocatedEarningRates();

        this.payrollMgmt.sortEmployeePays(this.payroll.EmployeePays, this.configSetupData);
        this.payrollMgmt.sortPaylinesBySeq(this.payroll);

        if (this.payrollSummaryService.PayrollHasErrors(this.payroll)) {
          this.hasCriticalErrors = true;
        } else {
          this.payrollSummary = this.payrollSummaryService.CalculatePayrollTotals(
            this.payroll,
            this.configSetupData
          );
          this.orderedLists = this.payrollSummaryService.GenerateOrderedLists(
            this.payrollSummary,
            this.configSetupData,
            this.masterTaxMap
          );
        }

        this._generateThirdPartyPayeeReport();

        this.loaded = true;
      });
  }

  private _generateThirdPartyPayeeReport() {
    // loop through all deductions in all the paylines
    // and check if any have any third party payee association(s)
    let thirdPartyPayees = this.payrollSummaryService.GetThirdPartyPayeeAssociations(this.payroll);

    let deductionMap = MapBy(this.configSetupData.Deductions, deduction => deduction.Id);

    if (thirdPartyPayees.length) {
      // we have third party associations tied to this payroll
      // build out our third party report
      this.companyService.getThirdPartyPayees(this.clientCode).subscribe(resp => {
        thirdPartyPayees.forEach(tp => {
          let thirdPartyPayee = resp.find(thirdParty => thirdParty.Id == tp.id);

          if (
            !thirdPartyPayee ||
            tp.deduction.CurrentAmount <= 0 ||
            tp.deduction.CurrentAmount == null ||
            tp.deduction.HoldThirdPartyDisb
          ) {
            return;
          }

          let thirdPartyPayeeReport = new ThirdPartyPayeeReport();
          thirdPartyPayeeReport.Amount = tp.deduction.CurrentAmount;
          thirdPartyPayeeReport.Description = thirdPartyPayee.Description;
          thirdPartyPayeeReport.Payee = thirdPartyPayee.Name;

          const config = deductionMap[tp.deduction.DeductionCode];

          if (!config) {
            console.log(
              `no deduction config found for deduction code:${tp.deduction.DeductionCode}`
            );
            return;
          }

          thirdPartyPayeeReport.AppearanceSequence = config.AppearanceSequence;
          thirdPartyPayeeReport.PaidBy = config.DisbursementAtEmployee
            ? `${this.payrollMgmt.getEmployeeFullName(tp.pay)}`
            : 'Combined - See Report';

          switch (thirdPartyPayee.PaymentMethod) {
            case 1:
              thirdPartyPayeeReport.Method = 'Check';
              break;
            case 2:
              thirdPartyPayeeReport.Method = 'ACH';
              break;
          }

          this.thirdPartyPayeeReports.push(thirdPartyPayeeReport);
        });

        this.thirdPartyPayeeReports.sort(
          (a: any, b: any) => a.AppearanceSequence - b.AppearanceSequence
        );
      });
    }
  }

  private _getDueDate() {
    return this.payrollService.isInPrefundWindow(
      this.clientCode,
      this.payroll.Year,
      this.payroll.SequenceNumber
    );
  }

  private _handleSubmit(content) {
    this._auditSubmitApproval();

    // create the "approved" version of the shadow
    // for audit comparison purposes
    this.payroll.Status = PayrollStatuses.Approved;
    this.payrollService.createPayrollShadow(this.payroll).subscribe(() => {
      // We must retrieve the manual checks first,
      // if we do not, they may be destroyed when we submit the payroll
      concat([this._auditManualChecks(), this._submitApproval(content)]);
    });
  }

  // Task #19257. if a rated earning has been allocated by,
  // hide the rate on the preview report (not labor allocations)
  // as that rate will no longer be accurate
  private _hideAllocatedEarningRates() {
    for (let pay of this.payroll.EmployeePays) {
      for (let line of pay.Paylines) {
        for (let e of line.Earnings) {
          const config = this.configSetupData.Earnings.find(x => x.Id == e.EarningId);
          if (this.payrollMgmt.earningRateAllocated(line, config)) {
            e.PermanentRateAmount = e.TemporaryRateAmount = null;
          }
        }
      }
    }
  }

  public _setPayFrequency() {
    this.freqDisplay = this.payrollMgmt.payrollFrequencyDisplay(this.payroll.EmployeePays, true);
  }

  private _showSubmitApprovalModal(content) {
    this.ngModalService.open(content, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false
    });
  }

  private _submitApproval(content) {
    return this.payrollService
      .getPayrollInfo(this.clientCode, this.year, this.seqNo)
      .pipe(
        concatMap(payrollInfo => {
          return this.payrollService
            .submitApproval(
              this.clientCode,
              this.year,
              this.seqNo,
              payrollInfo.SuppressNotifications
            )
            .pipe(
              catchError(err => {
                this.auditService.auditPayroll(
                  `payroll-approval-failed`,
                  {
                    ClientCode: this.clientCode,
                    PayrollId: this.payroll.Id,
                    PayDate: this.payroll.PayDate,
                    LocationId: this.payroll.LocationId,
                    SequenceNumber: this.seqNo,
                    PayrollType: this.payroll.PayType,
                    Calendars: this.payroll.Calendars
                  },
                  {
                    Year: this.year,
                    Error: err
                  }
                );

                return of(null);
              })
            );
        })
      )
      .subscribe((response: ApiResponse<PayrollApprovalTotals>) => {
        // if an error is caught above, do nothing
        if (!response) {
          return;
        }

        this.payrollApprovalTotals = response.Model;
        const now = new Date();
        const endOfday = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          this.env.endOfDayCutoff.hours,
          this.env.endOfDayCutoff.minutes
        );
        if (now > endOfday && !this.isCsr) {
          const warning: ErrorDetail = {
            WarningLevel: WarningLevel.Warning,
            Message:
              'Payrolls submitted after the end of the day will not be processed until the next business day.',
            FieldName: ''
          };
          response.Messages.push(warning);
        }

        // display all error, warning and info messages
        // on success and error modals (Bug 8455)
        this.responseMessages = FlattenResponseMessagesByTypes(
          [WarningLevel.Critical, WarningLevel.Informational, WarningLevel.Warning],
          response.Messages
        );

        if (!this.payrollApprovalTotals && response.Messages) {
          // show error modal
          this.isPayrollApproved = false;
        } else if (this.payrollApprovalTotals) {
          // success
          this._calculateApprovalTotals();
        }

        this._auditSubmitApprovalResponse(response);

        this.responseMessages.sort((a, b) => a.WarningLevel - b.WarningLevel);
        this._showSubmitApprovalModal(content);
      });
  }

  //#endregion
}
